import React, { useCallback, useState } from "react";
import styled from "styled-components";
import {
  Flex,
  Text,
  Box,
  Button,
  Tooltip,
  AssetType,
  ItemType,
  useClipboard,
  useNavigation,
  useWindowSize,
  useBoolean,
} from "@thenounproject/lingo-core";

import { Inspectable, InspectorSource } from "@constants/Inspector";

import { useViewModeContext } from "@contexts/ViewModeProvider";
import ActionsMenu, { useActionMenuTrigger } from "@features/context-menus/ActionMenu";
import { AssetExportController, ExportContext } from "@features/asset-export";
import { useActionById } from "@src/features/context-menus/ActionMenu/registry/useRegisteredActions";
import { useActionContext } from "@src/features/context-menus/ActionMenu/useActionContext";

const DownloadAssetWrapper = styled(Box).attrs({ ml: "l" })``;
const TitleText = styled(Text).attrs({
  font: "ui.regularBold",
})`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const ControlsContainer = styled(Flex).attrs({ alignItems: "center" })`
  flex-shrink: 0;
`;

const ActionButton = styled(Button).attrs({
  size: "small",
  mr: "s",
  themeOverrides: {
    primaryColor: "grayLightest",
    primaryColorTint: "black",
    primaryColorDark: "grayLight",
  },
})``;

export type Props = {
  inspectable: Inspectable;
  source: InspectorSource;
  sharePath: string;
  canEdit: boolean;
};
const InspectorSimple: React.FC<Props> = ({ inspectable, sharePath, canEdit }) => {
  const navigation = useNavigation();
  const { belowInspectorThreshold } = useWindowSize();
  const { setInspectorMode } = useViewModeContext();
  const { onCopy, hasCopied } = useClipboard(inspectable.asset?.meta?.content?.url || null);

  const [didCopyShareLink, setDidCopyShareLink] = useState(false);
  const [isActionMenuOpen, openActionMenu, closeActionMenu] = useBoolean(false);
  const [exportContext, setExportContext] = useState<ExportContext | null>(null);

  const actionContext = useActionContext({ inspectables: [inspectable] });
  const copyShareLink = useActionById("copy-share-link", actionContext);

  useActionMenuTrigger(() => {
    openActionMenu();
    return true;
  });

  const _copyShareLink = useCallback(() => {
    void copyShareLink.perform();
    setDidCopyShareLink(true);
    setTimeout(() => {
      setDidCopyShareLink(false);
    }, 1500);
  }, [copyShareLink]);

  const handleDownloadClick = useCallback(
    (e: React.MouseEvent) => {
      setExportContext({
        targetInspectable: inspectable,
        selectedInspectables: [inspectable],
        coordinates: { x: e.clientX, y: e.clientY },
        canEdit,
      });
    },
    [inspectable, canEdit]
  );

  const closeExportController = useCallback(() => {
    setExportContext(null);
  }, []);

  function renderTitleSection() {
    let title = "1 item selected";
    if (inspectable.asset) {
      title =
        inspectable.asset.name ||
        inspectable.item?.data?.viewName ||
        inspectable.item?.data?.name ||
        inspectable.asset.meta.font?.displayName ||
        "Unnamed asset";
    }
    return <TitleText>{title}</TitleText>;
  }

  function renderActionButton() {
    return (
      <>
        <ActionsMenu
          open={isActionMenuOpen}
          close={closeActionMenu}
          source="action-menu-button"
          inspectables={[inspectable]}
          vPos="alignBottom"
          hPos="alignLeft"
        />
        <Tooltip source="action-menu-button" direction={Tooltip.Direction.Top}>
          Actions (⌘K)
        </Tooltip>
        <ActionButton
          data-tooltip-source="action-menu-button"
          data-popup-source="action-menu-button"
          aria-label="Actions"
          icon="navigation.overflow"
          onClick={openActionMenu}
        />
      </>
    );
  }

  function viewDetailsButton() {
    return (
      <>
        <Tooltip source="view-details-button" direction={Tooltip.Direction.Top}>
          View details
        </Tooltip>
        <ActionButton
          data-tooltip-source="view-details-button"
          aria-label="View details"
          icon="info.info"
          onClick={() => setInspectorMode("detailed")}
        />
      </>
    );
  }
  function viewButton() {
    if (belowInspectorThreshold) return null;
    if (inspectable?.item?.type !== ItemType.asset || !inspectable.asset) return null;
    return (
      <>
        <Tooltip source="open-preview" direction={Tooltip.Direction.Top}>
          Open preview
        </Tooltip>
        <ActionButton
          data-tooltip-source="open-preview"
          aria-label="Open preview"
          icon="maximize"
          onClick={() => navigation.push(sharePath)}
        />
      </>
    );
  }
  function copyShareLinkButton() {
    return (
      <>
        <Tooltip source="copy-share-link-button" direction={Tooltip.Direction.Top}>
          {didCopyShareLink ? "Link copied" : "Copy link"}
        </Tooltip>
        <ActionButton
          data-tooltip-source="copy-share-link-button"
          icon="link"
          aria-label="Copy share link"
          onClick={_copyShareLink}
        />
      </>
    );
  }

  function downloadButton() {
    if (inspectable.item && inspectable.item.type !== ItemType.asset) return null;

    switch (inspectable.asset.type) {
      case AssetType.color:
        return (
          <DownloadAssetWrapper>
            <Button
              size="small"
              id="color-copy-button"
              text="Copy as"
              icon="triangle-double"
              width="100%"
              onClick={handleDownloadClick}
            />
          </DownloadAssetWrapper>
        );
      case AssetType.URL:
        return (
          <DownloadAssetWrapper>
            <Button size="small" text={hasCopied ? "Copied" : "Copy link"} onClick={onCopy} />
          </DownloadAssetWrapper>
        );
      default:
        if (inspectable.item?.displayProperties?.allowDownload === false) return null;
        return (
          <DownloadAssetWrapper>
            <Button
              size="small"
              text="Download"
              icon="download"
              width="100%"
              onClick={handleDownloadClick}
            />
          </DownloadAssetWrapper>
        );
    }
  }

  return (
    <>
      <AssetExportController context={exportContext} onClose={closeExportController} />
      <Flex
        justifyContent="space-between"
        alignItems="center"
        width="100%"
        data-testid="inspector-simple">
        {renderTitleSection()}
        <ControlsContainer>
          {viewButton()}
          {copyShareLinkButton()}
          {viewDetailsButton()}
          {renderActionButton()}
          {downloadButton()}
        </ControlsContainer>
      </Flex>
    </>
  );
};

export default InspectorSimple;
